import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import AtlasKitProgressBar from "@atlaskit/progress-bar";
import { R400 } from "@atlaskit/theme/colors";

import { useCloudStatus } from "../hooks";
import { DEFAULT_TIMEOUT } from "../hooks/utils/types";
import { getProgressbarStatus } from "../hooks/utils/use-cloud-status-utils";

const progressStyle = css({
    textAlign: "center",
    display: "flex",
    padding: "0",
    fontSize: "1rem",
    flexDirection: "column",
    fontWeight: "400",
    justifyContent: "center",
    alignSelf: "center",
    width: "100%",
    maxWidth: "400px",
    margin: "16px",
    alignItems: "center"
});

type Props = {
    isSuccessPreconditionsMet?: boolean;
};

export const ProgressBar = ({ isSuccessPreconditionsMet = true }: Props) => {
    const { createCloudStatus } = useCloudStatus();
    const [progress, setProgress] = useState(0);

    // components
    const progressTimeout = (
        <AtlasKitProgressBar
            testId="progress-timeout"
            value={1}
            theme={(getTokens, props) => {
                const tokens = getTokens(props);
                return {
                    ...tokens,
                    bar: {
                        ...tokens.bar,
                        background: R400
                    }
                };
            }}
        />
    );
    const progressPending = (
        <AtlasKitProgressBar testId="progress-pending" value={progress} />
    );

    // Handles the progress bar update
    useEffect(() => {
        if (createCloudStatus === "success" && isSuccessPreconditionsMet) {
            setProgress(1);
            return;
        }

        if (createCloudStatus === "success" && !isSuccessPreconditionsMet) {
            setProgress(0.8);
            return;
        }

        const startTime = Date.now();
        const pivotPercentage = 0.8; // The progress bar will fill up to 80% by the pivot time
        const progressBarPivotPoint = 60 * 1000;
        const slowProgressIncrement =
            (1 - pivotPercentage) /
            ((DEFAULT_TIMEOUT - progressBarPivotPoint) / 100);
        const slowProgress = 0;

        const interval = setInterval(() => {
            setProgress(
                getProgressbarStatus({
                    startTime,
                    pivotPercentage,
                    progressBarPivotPoint,
                    slowProgressIncrement,
                    slowProgress,
                    interval
                })
            );
        }, 100); // Update progress every 0.1 second
        return () => {
            clearInterval(interval);
        };
    }, [createCloudStatus, isSuccessPreconditionsMet]);

    return (
        <div css={progressStyle}>
            {createCloudStatus === "timeout"
                ? progressTimeout
                : progressPending}
        </div>
    );
};

export default ProgressBar;
